import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero } from '@/components/PageShell';

const TERMS_DESCRIPTION =
  "Sport Fund terms of service — rules and guidelines for using our athlete-fundraising platform.";

export default function Terms() {
  usePageMeta('Terms', TERMS_DESCRIPTION);
  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Terms of service"
          title="The rules of the platform."
          lead="Written in plain language. The short version: be honest about who you are and what you're raising for, don't break the law, and we'll all get along."
          size="sm"
        />
      }
    >
      <section className="bg-white">
        <article className="mx-auto max-w-3xl px-6 py-20 md:py-28 prose prose-slate prose-lg prose-headings:font-extrabold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:leading-relaxed prose-a:text-blue-700 prose-a:font-medium hover:prose-a:text-blue-800 prose-strong:text-slate-900">
          <p className="text-sm text-slate-500">
            <em>Sport Fund is operated by BackerBase LLC, a Wyoming limited liability company, 1621 Central Avenue, Cheyenne, WY 82001, USA.</em>
            <br />
            <strong>Last updated:</strong> 29 September 2026
          </p>
          <p>
            Welcome to Sport Fund. By accessing or using our website and services, you agree to be bound by these Terms of Service. Please read them carefully.
          </p>
          <h2>1. Acceptance of terms</h2>
          <p>
            By using Sport Fund, you agree to comply with and be bound by these Terms. If you don't agree, please don't use our services.
          </p>
          <h2>2. Eligibility</h2>
          <p>
            You must be at least 18 years old, or have the legal capacity to enter a contract in your jurisdiction, to use our services. By using Sport Fund, you represent that you meet these requirements.
          </p>
          <h2>3. Account registration</h2>
          <p>
            To access certain features, you'll create an account. You agree to provide accurate, complete, and current information, and to keep it updated. You're responsible for maintaining the confidentiality of your account credentials and for all activity under your account.
          </p>
          <h2>4. Use of services</h2>
          <p>Our <Link to="/acceptable-use">Acceptable Use Policy</Link> forms part of these Terms and lists what may not be raised for on the platform. You agree to use Sport Fund only for lawful purposes and in accordance with these Terms. You may not use our services to:</p>
          <ul>
            <li>Engage in illegal or fraudulent activity.</li>
            <li>Violate any applicable laws or regulations.</li>
            <li>Infringe on the rights of others, including intellectual property rights.</li>
            <li>Post or transmit harmful, offensive, or inappropriate content.</li>
            <li>Interfere with the operation of our website or services.</li>
          </ul>
          <h2>5. Fundraising and contributions</h2>
          <p>
            <strong>Fundraising:</strong> Sport Fund lets athletes create fund pages for sport expenses — equipment, competition travel, coaching, entry fees and related activities. You agree to provide truthful information about your fundraising and to use the funds for the stated purpose.
          </p>
          <p>
            <strong>Contributions:</strong> Contributions through Sport Fund are voluntary and generally non-refundable. We don't guarantee any specific amount will be raised.
          </p>
          <h2>6. Fees and payments</h2>
          <p>Creating and running a fundraising page on Sport Fund is free: there is no setup fee and no monthly subscription. BackerBase LLC charges a platform fee on each contribution of 7% of the contribution amount for card payments and 5% for bank debits (ACH in the US, SEPA in Europe).</p>
          <p>The platform fee is added on top of the amount the supporter chooses, and the total is shown before payment, so the fundraiser receives the full amount the supporter chose. For a one-off contribution, the platform fee is collected as an application fee on that payment; for a monthly contribution, it is collected in the same way from each monthly payment.</p>
          <p>Payments are processed by Stripe and transferred to the fundraiser's own Stripe account. Stripe's payment-processing fees are paid by BackerBase LLC out of the platform fee: they are not charged to the supporter in addition to the platform fee and are not deducted from the fundraiser's payout.</p>
          <h2>7. Content and intellectual property</h2>
          <p>
            <strong>Your content:</strong> You keep ownership of content you submit. You grant us a non-exclusive, royalty-free, worldwide license to use, reproduce, distribute and display it in connection with our services.
          </p>
          <p>
            <strong>Our content:</strong> All Sport Fund content — text, graphics, logos, software — is our property or our licensors', protected by copyright and trademark laws. You may not use, copy or distribute it without our written permission.
          </p>
          <h2>8. Third-party services</h2>
          <p>
            Sport Fund may link to third-party services like Stripe. We don't control or endorse those services and aren't responsible for their content or practices. Use them at your own risk.
          </p>
          <h2>9. Termination</h2>
          <p>
            We may suspend or terminate your account or access to our services at any time, for any reason, without notice. These Terms continue to apply to any prior use of our services.
          </p>
          <h2>10. Limitation of liability</h2>
          <p>
            To the fullest extent permitted by law, Sport Fund and its affiliates, officers, employees, and agents are not liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of our services.
          </p>
          <h2>11. Disclaimers</h2>
          <p>
            Our services are provided "as is" and "as available", without warranties of any kind. We don't guarantee uninterrupted or error-free service, or the accuracy or completeness of content on our website.
          </p>
          <h2>12. Governing law</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of the State of Wyoming, USA, without regard to its conflict of law principles. Any disputes arising out of or related to these Terms or our services will be resolved exclusively in the state or federal courts located in Laramie County, Wyoming, USA. If you use our services as a consumer, this does not deprive you of the protection of the mandatory laws of the country where you live, or of any right you have under those laws to bring proceedings in your local courts.
          </p>
          <h2>13. Changes to these terms</h2>
          <p>
            We may update these Terms from time to time. We'll post the new Terms here and update the "Last updated" date above. Continued use after changes means you accept them.
          </p>
          <h2>14. Contact us</h2>
          <p>
            Questions or concerns about these Terms? Email us at <a href="mailto:info@thesportfund.com">info@thesportfund.com</a>.
          </p>
        </article>
      </section>
    </PageShell>
  );
}
